import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL, HOME } from "../../../../utils/CONSTANTS";
import { setLoginAudit } from "../../../../utils/msal-utils";
import DotGlobe from "./DotGlobe";

/**
 * AuthComplete — finalizes the OIDC sign-in started at /api/login.
 *
 * Why this exists:
 *   The .NET API no longer stores tokens in a per-instance IMemoryCache (which broke
 *   under Cloud Run horizontal scaling — token written to instance A, /api/getdetails
 *   read attempt hit instance B → "Token Details Not Found"). Instead, /api/ returns
 *   a tiny HTML handoff page that stashes an AES-GCM-encrypted blob in sessionStorage
 *   and redirects here. This component reads the blob and exchanges it for tokens via
 *   /api/getdetails, which can be served by ANY instance because decryption is stateless.
 *
 * UI: renders the branded "Signing you in" screen (dark dot-globe) from the Vivid
 *   mockup while the exchange runs — replaces the old global .divSpinner for this route.
 *
 * Flow:
 *   1. Read blob from sessionStorage (primary) or URL fragment (fallback for blocked storage)
 *   2. GET /api/getdetails?id={blob} — same endpoint as before, just a different id format
 *   3. Persist tokens to localStorage (matches existing SPA convention)
 *   4. Navigate to HOME FIRST
 *   5. Fire setLoginAudit (whose window.location.reload() then reloads /home, not us)
 */
const AuthComplete = () => {
  const navigate = useNavigate();
  const didRun = useRef(false);

  useEffect(() => {
    // StrictMode invokes effects twice in dev. Without this guard we double-call
    // /api/getdetails — harmless on success but spams server logs and the audit endpoint.
    if (didRun.current) return;
    didRun.current = true;

    const run = async () => {
      // 1. Source the blob. sessionStorage is primary; URL fragment is fallback for
      //    private mode / strict ITP where sessionStorage.setItem can throw.
      let blob: string | null = null;
      try {
        blob = sessionStorage.getItem("__ttec_auth_blob");
      } catch {
        // sessionStorage access blocked — fall through to fragment.
      }
      if (!blob && window.location.hash.startsWith("#b=")) {
        blob = decodeURIComponent(window.location.hash.slice(3));
      }

      if (!blob) {
        // No blob anywhere — user probably hit /auth/complete directly. Restart auth.
        console.warn("[AuthComplete] No auth blob found; restarting login.");
        window.location.replace(BASE_URL + "api/login");
        return;
      }

      // 2. Clear the blob BEFORE exchanging so a reload of /auth/complete can't replay it.
      try {
        sessionStorage.removeItem("__ttec_auth_blob");
      } catch {
        /* ignore */
      }

      try {
        const resp = await axios.get(
          BASE_URL + "api/getdetails?id=" + encodeURIComponent(blob),
          { headers: { "Content-Type": "application/json" } }
        );

        if (resp?.data?.httpStatus !== 200 || !resp?.data?.data) {
          // Blob expired, tampered, or backend rejected. Restart auth.
          console.warn("[AuthComplete] getdetails rejected blob:", resp?.data);
          window.location.replace(BASE_URL + "api/login");
          return;
        }

        const data = resp.data.data;

        // 3. Persist tokens. Key names and "Bearer " prefix match login.tsx so the rest
        //    of the SPA (axios interceptor, protected routes, setLoginAudit) keeps working.
        if (data.accessToken)
          localStorage.setItem("AccessToken", "Bearer " + data.accessToken);
        if (data.refreshToken)
          localStorage.setItem("RefreshToken", data.refreshToken);
        if (data.idToken) localStorage.setItem("IdToken", data.idToken);

        // 4. Navigate to HOME BEFORE firing setLoginAudit.
        //    Critical: setLoginAudit ends with window.location.reload(). If we awaited
        //    it from /auth/complete, the reload would reload /auth/complete — which by
        //    then has no blob in sessionStorage, so this component would loop back to
        //    /api/login. Navigating first ensures the reload reloads /home instead.
        navigate(HOME, { replace: true });

        // 5. Fire setLoginAudit AFTER navigate. Not awaited — the navigate above is
        //    what matters for UX. The audit's eventual reload of /home is harmless.
        if (data.accessToken) {
          setLoginAudit("Bearer " + data.accessToken, "Login");
        }
      } catch (err) {
        console.error("[AuthComplete] getdetails failed:", err);
        window.location.replace(BASE_URL + "api/login");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Branded "Signing you in" screen (Vivid mockup s-sso). Dark globe + progress.
  return (
    <div className="ssoScreen" aria-live="polite">
      <style>{`
        .ssoScreen{position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;
          align-items:center;justify-content:center;text-align:center;gap:22px;
          background:linear-gradient(160deg,#012a57 0%,#01193a 55%,#020e22 100%);
          color:#eaf3ff;overflow:hidden;font-family:"TTEC-Sans","Open Sans",system-ui,sans-serif}
        .ssoScreen::before{content:"";position:absolute;inset:0;
          background:radial-gradient(50% 40% at 50% 35%,rgba(4,149,255,.35),transparent 70%),
                     radial-gradient(40% 30% at 50% 30%,rgba(101,255,255,.25),transparent 70%);
          animation:ssoPulse 3s ease-in-out infinite}
        @keyframes ssoPulse{0%,100%{opacity:.7}50%{opacity:1}}
        .ssoScreen>*{position:relative;z-index:2}
        .ssoScreen .ssoLogo{display:inline-flex;align-items:center;gap:9px;font-weight:700;
          letter-spacing:.02em;font-size:15px;color:#eaf3ff}
        .ssoScreen .ssoLogo span{color:#65ffff}
        .ssoScreen h2{font-size:24px;font-weight:600;margin:0}
        .ssoScreen p{color:#9db8dd;font-size:13.5px;max-width:30ch;margin:0}
        .ssoScreen .ssoBar{width:220px;height:5px;border-radius:6px;
          background:rgba(160,200,255,.16);overflow:hidden}
        .ssoScreen .ssoBar i{display:block;height:100%;width:40%;border-radius:6px;
          background:linear-gradient(120deg,#0495ff 0%,#3cadff 55%,#65ffff 100%);
          animation:ssoLoad 1.6s ease-in-out infinite}
        @keyframes ssoLoad{0%{margin-left:-40%}100%{margin-left:100%}}
      `}</style>
      <DotGlobe className="ssoGlobe" size={120} variant="dark" />
      <div className="ssoLogo">
        TTEC&nbsp;<span>Connect</span>
      </div>
      <h2>Signing you in…</h2>
      <p>Verifying your credentials with TTEC single sign-on.</p>
      <div className="ssoBar" aria-hidden="true">
        <i />
      </div>
    </div>
  );
};

export default AuthComplete;
